import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import {
  fetchEmotionalStateCard,
  fetchTodayDailyPlan,
  fetchExistingRecord,
  fetchEmotionGroups,
  registerEmotion,
} from '../services/healthService';

// ─── Constantes (port del widget Flutter) ───────────────────────────────────

const EMOTION_FALLBACK_IMAGE =
  'https://firebasestorage.googleapis.com/v0/b/mainbucketdaisies/o/health_answer%2Fimage_answer_22.png?alt=media';

const EASE_OUT_QUART = 'cubic-bezier(0.25, 1, 0.5, 1)';
const PREVIEW_COUNT = 6;

// Paletas de familia por sentimiento (copiadas del .dart).
const POSITIVE_PALETTE = [
  '#22C55E', '#34D399', '#4ADE80', '#2DD4BF', '#38BDF8',
  '#60A5FA', '#86EFAC', '#5EEAD4', '#7DD3FC',
];
const NEUTRAL_PALETTE = [
  '#FBBF24', '#FCD34D', '#F59E0B', '#FACC15', '#FDE68A',
  '#9CA3AF', '#B4B8C0', '#E0C56E', '#CBB994',
];
const NEGATIVE_PALETTE = [
  '#EF4444', '#FB923C', '#F87171', '#FDBA74', '#FB7185',
  '#EA580C', '#FCA5A5', '#F97316', '#DC2626',
];

const SENTIMENT_COLOR = {
  positive: '#22C55E',
  neutral: '#F59E0B',
  negative: '#EF4444',
};

const sentimentFromTitle = (title = '') => {
  const t = String(title).toLowerCase();
  if (t.includes('positiv')) return 'positive';
  if (t.includes('negativ')) return 'negative';
  return 'neutral';
};

const familyColor = (sentiment, index) => {
  const p =
    sentiment === 'positive'
      ? POSITIVE_PALETTE
      : sentiment === 'negative'
      ? NEGATIVE_PALETTE
      : NEUTRAL_PALETTE;
  return p[index % p.length];
};

// ─── Lectura defensiva de los campos (snake_case y camelCase) ────────────────

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const groupTitle = (group) => pick(group, 'title') || '';
const groupQuestionId = (group) => pick(group, 'id', 'question_id', 'questionId');

const groupAnswers = (group) => {
  const arr = pick(group, 'question_answer_ids', 'questionAnswerIds', 'answer_ids', 'answers');
  return Array.isArray(arr) ? arr : [];
};

const answerValue = (a) => pick(a, 'value', 'name') || '';
const answerImage = (a) => pick(a, 'url_image', 'urlImage', 'image') || '';
const answerColor = (a) => pick(a, 'answer_color', 'answerColor', 'color') || '';
const answerId = (a) => pick(a, 'id', 'value_options', 'valueOptions');
const answerSeq = (a) => pick(a, 'sequence') || 0;

const firstGroupImage = (group) => {
  for (const a of groupAnswers(group)) {
    const img = answerImage(a);
    if (img) return img;
  }
  return EMOTION_FALLBACK_IMAGE;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const FACE_SIZE = 38;

// ─── Face: la cara que asoma desde la esquina superior derecha ───────────────

function Face({ sentiment, color, imageUrl, tiltSign = 1, chipIndex = 0, reduced }) {
  const inner = (
    <img
      src={imageUrl || EMOTION_FALLBACK_IMAGE}
      alt=""
      draggable={false}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
      }}
    />
  );

  const base = {
    width: FACE_SIZE,
    height: FACE_SIZE,
    backgroundColor: color,
    overflow: 'hidden',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  let shape;
  if (sentiment === 'positive') {
    shape = (
      <div style={{ ...base, borderRadius: '9999px', padding: 6 }}>{inner}</div>
    );
  } else if (sentiment === 'negative') {
    shape = (
      <div style={{ ...base, borderRadius: 12, padding: 6 }}>{inner}</div>
    );
  } else {
    // neutral: triangulo apuntando hacia arriba con esquinas redondeadas.
    shape = (
      <div
        style={{
          ...base,
          padding: 4,
          paddingTop: FACE_SIZE * 0.18,
          clipPath:
            'polygon(50% 4%, 60% 8%, 96% 86%, 92% 96%, 8% 96%, 4% 86%, 40% 8%)',
        }}
      >
        {inner}
      </div>
    );
  }

  // Tilt estatico + wobble idle (rotacion + bob) salvo reduced-motion.
  const wobbleName = `mm-wobble-${chipIndex % 5}`;
  const animStyle = reduced
    ? { transform: `rotate(${0.18 * tiltSign}rad)` }
    : {
        transform: `rotate(${0.18 * tiltSign}rad)`,
        animation: `${wobbleName} ${2 + (chipIndex % 4) * 0.09}s ease-in-out ${
          0.12 * chipIndex
        }s infinite alternate`,
      };

  const tiltVar = { '--mm-tilt': `${0.18 * tiltSign}rad` };
  return <div style={{ ...tiltVar, ...animStyle }}>{shape}</div>;
}

// ─── Chip: pildora plana con la cara asomando ────────────────────────────────

function MoodChip({
  label,
  imageUrl,
  faceColor,
  sentiment,
  tiltSign,
  chipIndex,
  onTap,
  reduced,
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={onTap}
      onMouseDown={() => !reduced && setPressed(true)}
      onMouseUp={() => !reduced && setPressed(false)}
      onMouseLeave={() => !reduced && setPressed(false)}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'block',
        width: '100%',
        boxSizing: 'border-box',
        paddingTop: 8,
        paddingRight: 8,
        transition: reduced ? undefined : `transform 120ms ${EASE_OUT_QUART}`,
        transform: pressed ? 'scale(0.96)' : 'scale(1)',
      }}
    >
      <div style={{ position: 'relative' }}>
        <div
          style={{
            minHeight: 40,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 8,
            paddingTop: 4,
            paddingBottom: 4,
            paddingRight: FACE_SIZE - 4,
            backgroundColor: '#E8DCFB', // brand-tint
            borderRadius: 9999,
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 13,
              lineHeight: 1.2,
              color: '#1C1A23', // ink
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {label}
          </span>
        </div>
        <div style={{ position: 'absolute', top: -5, right: -5 }}>
          <Face
            sentiment={sentiment}
            color={faceColor}
            imageUrl={imageUrl}
            tiltSign={tiltSign}
            chipIndex={chipIndex}
            reduced={reduced}
          />
        </div>
      </div>
    </button>
  );
}

// ─── Staggered fade + slide-in wrapper ───────────────────────────────────────

function Staggered({ index, reduced, children }) {
  if (reduced) return <>{children}</>;
  return (
    <div
      style={{
        animation: `mm-enter 260ms ${EASE_OUT_QUART} ${40 * index}ms both`,
      }}
    >
      {children}
    </div>
  );
}

// ─── Summary card (estado ya-registrado y card in-place post registro) ───────

function SummaryCard({ value, imageUrl, faceColor, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        width: '100%',
        height: 120,
        borderRadius: 20,
        backgroundColor: faceColor,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <img
        src={imageUrl || EMOTION_FALLBACK_IMAGE}
        alt=""
        draggable={false}
        style={{
          position: 'absolute',
          right: -16,
          bottom: -12,
          width: 148,
          height: 148,
          objectFit: 'contain',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          paddingLeft: 16,
          paddingTop: 16,
          paddingBottom: 16,
          paddingRight: 100,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            fontSize: 12,
            color: 'rgba(28,26,35,0.65)',
            marginBottom: 4,
          }}
        >
          Monitoreo
        </span>
        <span style={{ fontSize: 20, fontWeight: 700, color: '#1C1A23', lineHeight: 1.2 }}>
          Hoy me siento {value}
        </span>
      </div>
    </div>
  );
}

// ─── Picker (generales -> especificos) ──────────────────────────────────────

function MoodGroupPicker({ groups, card, userId, authToken, reduced, onRegistered }) {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const expand = (i) => {
    setExpandedIndex(i);
    setShowAll(false);
  };
  const collapse = () => {
    setExpandedIndex(null);
    setShowAll(false);
  };

  const handleRegister = async (group, answer, sentiment, index) => {
    if (busy) return;
    setBusy(true);
    setErrorMsg('');
    const res = await registerEmotion({
      userId,
      cardId: card.id,
      questionId: groupQuestionId(group),
      answerId: answerId(answer),
      authToken,
    });
    setBusy(false);
    if (!res.success) {
      setErrorMsg('Ha ocurrido un error. Por favor inténtalo de nuevo.');
      return;
    }
    onRegistered({
      value: answerValue(answer),
      imageUrl: answerImage(answer) || EMOTION_FALLBACK_IMAGE,
      faceColor: familyColor(sentiment, index),
    });
  };

  let content;
  if (expandedIndex == null) {
    content = (
      <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
        {groups.map((group, i) => {
          const sentiment = sentimentFromTitle(groupTitle(group));
          return (
            <React.Fragment key={i}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Staggered index={i} reduced={reduced}>
                  <MoodChip
                    label={groupTitle(group)}
                    imageUrl={firstGroupImage(group)}
                    faceColor={SENTIMENT_COLOR[sentiment]}
                    sentiment={sentiment}
                    tiltSign={i % 2 === 0 ? 1 : -1}
                    chipIndex={i}
                    reduced={reduced}
                    onTap={() => expand(i)}
                  />
                </Staggered>
              </div>
              {i < groups.length - 1 && <div style={{ width: 8 }} />}
            </React.Fragment>
          );
        })}
      </div>
    );
  } else {
    const group = groups[expandedIndex];
    const sentiment = sentimentFromTitle(groupTitle(group));
    const answers = [...groupAnswers(group)].sort((a, b) => answerSeq(a) - answerSeq(b));
    const hasOverflow = answers.length > PREVIEW_COUNT;
    const visible = hasOverflow && !showAll ? answers.slice(0, PREVIEW_COUNT) : answers;

    content = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <button
          type="button"
          onClick={collapse}
          style={{
            all: 'unset',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            paddingTop: 4,
            paddingBottom: 4,
            maxWidth: '100%',
          }}
        >
          <ChevronLeft size={20} color="#786ED9" />
          <span style={{ fontWeight: 600, color: '#786ED9' }}>Volver</span>
          <span
            style={{
              marginLeft: 4,
              color: '#6b7280',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {groupTitle(group)}
          </span>
        </button>
        <div style={{ height: 8 }} />
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            overflowX: 'auto',
            maxWidth: '100%',
            gap: 8,
            paddingBottom: 4,
          }}
        >
          {visible.map((answer, i) => (
            <div key={i} style={{ flex: '0 0 auto' }}>
              <Staggered index={i} reduced={reduced}>
                <MoodChip
                  label={answerValue(answer)}
                  imageUrl={answerImage(answer) || EMOTION_FALLBACK_IMAGE}
                  faceColor={familyColor(sentiment, i)}
                  sentiment={sentiment}
                  tiltSign={i % 2 === 0 ? 1 : -1}
                  chipIndex={i}
                  reduced={reduced}
                  onTap={() => handleRegister(group, answer, sentiment, i)}
                />
              </Staggered>
            </div>
          ))}
          {hasOverflow && !showAll && (
            <div style={{ flex: '0 0 auto' }}>
              <Staggered index={visible.length} reduced={reduced}>
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    marginTop: 8,
                    minHeight: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingLeft: 16,
                    paddingRight: 16,
                    backgroundColor: 'rgba(120,110,217,0.08)',
                    borderRadius: 9999,
                    fontWeight: 600,
                    color: '#786ED9',
                  }}
                >
                  Ver más
                </button>
              </Staggered>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {content}
      {errorMsg && (
        <div style={{ marginTop: 8, fontSize: 13, color: '#EF4444' }}>{errorMsg}</div>
      )}
    </div>
  );
}

// ─── Keyframes inyectadas una vez ────────────────────────────────────────────

function MoodMonitorStyles() {
  return (
    <style>{`
@keyframes mm-enter {
  0% { opacity: 0; transform: translateY(7px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes mm-wobble-0 { 0% { transform: rotate(calc(var(--mm-tilt) - 0.04rad)) translateY(-2px); } 100% { transform: rotate(calc(var(--mm-tilt) + 0.04rad)) translateY(2px); } }
@keyframes mm-wobble-1 { 0% { transform: rotate(calc(var(--mm-tilt) - 0.04rad)) translateY(-2px); } 100% { transform: rotate(calc(var(--mm-tilt) + 0.04rad)) translateY(2px); } }
@keyframes mm-wobble-2 { 0% { transform: rotate(calc(var(--mm-tilt) - 0.04rad)) translateY(-2px); } 100% { transform: rotate(calc(var(--mm-tilt) + 0.04rad)) translateY(2px); } }
@keyframes mm-wobble-3 { 0% { transform: rotate(calc(var(--mm-tilt) - 0.04rad)) translateY(-2px); } 100% { transform: rotate(calc(var(--mm-tilt) + 0.04rad)) translateY(2px); } }
@keyframes mm-wobble-4 { 0% { transform: rotate(calc(var(--mm-tilt) - 0.04rad)) translateY(-2px); } 100% { transform: rotate(calc(var(--mm-tilt) + 0.04rad)) translateY(2px); } }
@media (prefers-reduced-motion: reduce) {
  .mm-root *, .mm-root *::before, .mm-root *::after {
    animation: none !important;
    transition: none !important;
  }
}
`}</style>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export default function MoodMonitor({ userId, authToken, tz }) {
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  const [groups, setGroups] = useState([]);
  // record existente al cargar => ya registro hoy. { value, imageUrl, faceColor }
  const [registeredSummary, setRegisteredSummary] = useState(null);
  const mounted = useRef(true);
  const reduced = useMemo(() => prefersReducedMotion(), []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const c = await fetchEmotionalStateCard();
      if (cancelled) return;
      if (!c) {
        setCard(null);
        setLoading(false);
        return;
      }
      setCard(c);

      const dailyPlan = await fetchTodayDailyPlan(userId, c.id);
      if (cancelled) return;

      let record = null;
      if (dailyPlan && dailyPlan.id != null) {
        record = await fetchExistingRecord(dailyPlan.id, userId, c.id);
      }
      if (cancelled) return;

      if (record) {
        // Resolver answer del record de forma defensiva.
        const ansRoot =
          pick(record, 'answer_ids', 'answerIds') ||
          pick(record, 'answers') ||
          [];
        const firstAns = Array.isArray(ansRoot) ? ansRoot[0] : ansRoot;
        const suggested =
          pick(firstAns, 'suggested_answer_id', 'suggestedAnswerId') || firstAns || {};
        const value = answerValue(suggested) || pick(record, 'value') || '';
        const imageUrl = answerImage(suggested) || EMOTION_FALLBACK_IMAGE;
        // El record no trae el indice de familia; usamos answer_color si viene
        // como color CSS valido, si no, el color de sentimiento neutro.
        const ac = answerColor(suggested);
        const faceColor = ac && /^#|rgb/.test(ac) ? ac : SENTIMENT_COLOR.neutral;
        setRegisteredSummary({ value, imageUrl, faceColor });
      } else {
        const g = await fetchEmotionGroups(c.id);
        if (cancelled) return;
        setGroups(g);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Render null mientras carga o si no existe la card (se oculta en silencio).
  if (loading || !card) return null;

  return (
    <div className="mm-root" style={{ fontFamily: '"Hanken Grotesk", sans-serif' }}>
      <MoodMonitorStyles />
      {registeredSummary ? (
        <div style={{ paddingTop: 15 }}>
          <SummaryCard
            value={registeredSummary.value}
            imageUrl={registeredSummary.imageUrl}
            faceColor={registeredSummary.faceColor}
          />
        </div>
      ) : groups.length === 0 ? null : (
        <div style={{ paddingBottom: 15 }}>
          <h2
            style={{
              color: '#3D3580', // brand-deep
              fontWeight: 700,
              fontSize: 22,
              lineHeight: 1.2,
              margin: 0,
              marginBottom: 12,
            }}
          >
            ¿Cómo te sientes hoy?
          </h2>
          <MoodGroupPicker
            groups={groups}
            card={card}
            userId={userId}
            authToken={authToken}
            reduced={reduced}
            onRegistered={(summary) => {
              // Card drenched in-place; tras un beat se queda como resumen.
              setRegisteredSummary({ ...summary });
            }}
          />
        </div>
      )}
    </div>
  );
}
